import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getClientIdCookie, getClientIdIndexedDb, persistClientIdBackups, setClientIdCookie } from './clientIdBackup';

export const CLIENT_ID_QR_PREFIX = '220km-client-id:';

export const CLIENT_ID_QR_APP_NATIVE_IOS = 'native-ios';
export const CLIENT_ID_QR_APP_NATIVE_ANDROID = 'native-android';
export const CLIENT_ID_QR_APP_WEB = 'web';

// Synchronous function that reads clientId from localStorage
// Should be used only after clientId has been generated
export const getClientId = () => {
  return localStorage.getItem('clientId');
};

export const getClientAgent = () => {
  const base = navigator.userAgent;
  if (typeof window !== 'undefined' && window.Capacitor?.getPlatform) {
    const platform = window.Capacitor.getPlatform();
    if (platform === 'ios') {
      return `220km-native-ios ${base}`;
    }
    if (platform === 'android') {
      return `220km-native-android ${base}`;
    }
  }
  // Legacy Android WebView injects AndroidInterface without a Capacitor platform.
  if (typeof window !== 'undefined' && window.AndroidInterface) {
    return `220km-native-android ${base}`;
  }
  return base;
};

/** True when running inside the Capacitor iOS/Android shell (or legacy Android WebView bridge). */
export const isNativeShellApp = () => {
  if (typeof window === 'undefined') return false;
  const cap = window.Capacitor;
  if (cap != null && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) {
    return true;
  }
  return Boolean(window.AndroidInterface);
};

function newCompactClientId() {
  return uuidv4().replace(/-/g, '');
}

/** OCPI roaming wallets: OCPI_<CC>_<PARTY> or OCPI_<CC>_<PARTY>_<token>. */
const OCPI_CLIENT_UI_ID_RE = /^OCPI_[A-Za-z0-9]{2}_[A-Za-z0-9]{1,10}(?:_[A-Za-z0-9]{1,32})?$/;

export function normalizeClientId(clientId) {
  const raw = String(clientId ?? '').trim();
  if (!raw) {
    return null;
  }

  // Keep OCPI ids as-is (do not strip OCPI_ or lowercase the token suffix).
  if (OCPI_CLIENT_UI_ID_RE.test(raw)) {
    return raw;
  }

  const normalized = raw
    .replace(/^#?[A-Za-z0-9]+_/, '')
    .replace(/-/g, '')
    .toLowerCase();

  return /^[a-f0-9]{32}$/.test(normalized) ? normalized : null;
}

function writeClientIdLocal(value) {
  localStorage.setItem('clientId', value);
}

export function setClientId(clientId) {
  const value = extractClientIdValue(clientId);
  if (!value) {
    return false;
  }
  writeClientIdLocal(value);
  persistClientIdBackups(value);
  return true;
}

function extractClientIdValue(clientId) {
  const normalized = normalizeClientId(clientId);
  if (normalized) {
    return normalized;
  }

  const raw = String(clientId ?? '').trim();
  return raw || null;
}

/** Shell that generated the QR: lets a scanner tell native from web without charging history. */
function currentClientIdQrApp() {
  if (typeof window !== 'undefined') {
    const platform = window.Capacitor?.getPlatform?.();
    if (platform === 'ios') {
      return CLIENT_ID_QR_APP_NATIVE_IOS;
    }
    if (platform === 'android') {
      return CLIENT_ID_QR_APP_NATIVE_ANDROID;
    }
  }
  if (isNativeShellApp()) {
    return CLIENT_ID_QR_APP_NATIVE_ANDROID;
  }
  return CLIENT_ID_QR_APP_WEB;
}

export function isNativeClientIdQrApp(app) {
  return app === CLIENT_ID_QR_APP_NATIVE_IOS || app === CLIENT_ID_QR_APP_NATIVE_ANDROID;
}

export function buildClientIdQrPayload(clientId) {
  const value = extractClientIdValue(clientId ?? getClientId());
  return value ? `${CLIENT_ID_QR_PREFIX}${value}?app=${currentClientIdQrApp()}` : '';
}

/** Payload without the `?app=` suffix stays valid: QRs printed by older app versions still scan. */
export function parseClientIdQrDetails(payload) {
  const raw = String(payload ?? '').trim();
  if (!raw) {
    return null;
  }

  if (raw.startsWith(CLIENT_ID_QR_PREFIX)) {
    const body = raw.slice(CLIENT_ID_QR_PREFIX.length);
    const queryStart = body.indexOf('?');
    const clientId = extractClientIdValue(queryStart === -1 ? body : body.slice(0, queryStart));
    if (!clientId) {
      return null;
    }

    let app = null;
    if (queryStart !== -1) {
      const value = new URLSearchParams(body.slice(queryStart + 1)).get('app');
      app = value ? value.trim().toLowerCase() : null;
    }

    return { clientId, app };
  }

  // Bare OCPI / 32-hex ids (family-share screenshot text, admin paste).
  const bare = normalizeClientId(raw);
  return bare ? { clientId: bare, app: null } : null;
}

export function parseClientIdQrPayload(payload) {
  const details = parseClientIdQrDetails(payload);
  return details ? details.clientId : null;
}

/**
 * Resolve clientId: localStorage → cookie → generate.
 * When generating, async IndexedDB may restore an older id and reload once.
 */
export function readOrCreateClientId() {
  const fromLocal = normalizeClientId(localStorage.getItem('clientId'));
  if (fromLocal) {
    persistClientIdBackups(fromLocal);
    return fromLocal;
  }

  const fromCookie = normalizeClientId(getClientIdCookie());
  if (fromCookie) {
    writeClientIdLocal(fromCookie);
    persistClientIdBackups(fromCookie);
    return fromCookie;
  }

  const generated = newCompactClientId();
  writeClientIdLocal(generated);
  try {
    setClientIdCookie(generated);
  } catch (_e) {
    // ignore
  }

  // IndexedDB may still hold the previous id after localStorage/cookie wipe.
  getClientIdIndexedDb()
    .then(raw => {
      const restored = normalizeClientId(raw);
      if (!restored || restored === generated) {
        persistClientIdBackups(generated);
        return;
      }
      writeClientIdLocal(restored);
      persistClientIdBackups(restored);
      if (typeof window !== 'undefined' && typeof window.location?.reload === 'function') {
        window.location.reload();
      }
    })
    .catch(() => {
      persistClientIdBackups(generated);
    });

  return generated;
}

// Persists a random 32-char hex clientId (no hyphens). Fingerprint Pro is not used.
// Returns { clientId, isLoading } — isLoading is always false once the hook has run.
export const useGetClientId = () => {
  const [clientId] = useState(() => readOrCreateClientId());
  return { clientId, isLoading: false };
};
